import React from 'react';
import { Clock, Plane } from 'lucide-react';
import {
  formatTime, formatDuration, layoverSummary, layoverWarnings,
  layovers, airportDetailLabel, SHORT_LAYOVER_MIN,
} from './flightData';

// 一个行程方向（去程/回程/第 N 程）的时刻轴。
// 往返票的四个航段若拉平成一串，用户看不出哪几段是回来的，所以每个 slice 独立渲染。
const SliceRow = ({ slice, label, expanded }) => {
  const segs = slice.segments || [];
  if (!segs.length) return null;

  const first = segs[0];
  const last = segs[segs.length - 1];
  const totalMin = segs.reduce((s, x) => s + (x.duration_minutes || 0), 0);
  const stops = segs.length - 1;
  const waits = layovers(segs);
  const warnings = layoverWarnings(segs);

  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        {label && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">
            {label}
          </span>
        )}
        <span className="text-sm text-gray-600">{first.airline_name_zh} {first.flight_number}</span>
        {stops > 0 && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
            中转 {stops} 次
          </span>
        )}
      </div>

      <div className="flex items-center gap-4">
        <div>
          <p className="text-2xl font-black text-gray-900">{formatTime(first.dep_time).split(' ')[1]}</p>
          <p className="text-xs text-gray-500">{first.dep_airport} · {formatTime(first.dep_time).split(' ')[0]}</p>
        </div>
        <div className="flex-1 flex flex-col items-center px-2">
          <p className="text-xs text-gray-400 flex items-center gap-1">
            <Clock className="w-3 h-3" /> {formatDuration(totalMin)}
          </p>
          <div className="w-full flex items-center gap-1 my-1">
            <span className="h-px flex-1 bg-gray-200" />
            <Plane className="w-4 h-4 text-sky-500" />
            <span className="h-px flex-1 bg-gray-200" />
          </div>
          <p className="text-xs text-gray-400">{layoverSummary(segs)}</p>
          {warnings.map((w) => (
            <p key={w} className="text-[11px] text-amber-600 font-medium mt-0.5 text-center">⚠ {w}</p>
          ))}
        </div>
        <div className="text-right">
          <p className="text-2xl font-black text-gray-900">{formatTime(last.arr_time).split(' ')[1]}</p>
          <p className="text-xs text-gray-500">{last.arr_airport} · {formatTime(last.arr_time).split(' ')[0]}</p>
        </div>
      </div>

      {expanded && stops > 0 && (
        <div className="mt-3 pl-1 text-xs space-y-1.5">
          {segs.map((s, i) => {
            const wait = waits[i];
            return (
              <React.Fragment key={i}>
                <p className="text-gray-500">
                  {s.flight_number}（{s.airline_name_zh}）
                  {airportDetailLabel(s.dep_airport)} {formatTime(s.dep_time)}
                  {' → '}
                  {airportDetailLabel(s.arr_airport)} {formatTime(s.arr_time)}
                  {' · '}{formatDuration(s.duration_minutes)}
                </p>
                {wait && (
                  <p className={`flex items-start gap-1 pl-4 ${
                    wait.changesAirport || (wait.minutes !== null && wait.minutes < SHORT_LAYOVER_MIN)
                      ? 'text-amber-600' : 'text-gray-400'
                  }`}>
                    <Clock className="w-3 h-3 flex-shrink-0 mt-0.5" />
                    {wait.changesAirport ? (
                      <span>
                        需转场：{airportDetailLabel(wait.code)} → {airportDetailLabel(wait.nextCode)}
                        {wait.minutes !== null && ` · 可用 ${formatDuration(wait.minutes)}`}
                      </span>
                    ) : (
                      <span>
                        在 {airportDetailLabel(wait.code)} 中转
                        {wait.minutes !== null && ` 等待 ${formatDuration(wait.minutes)}`}
                        {wait.minutes !== null && wait.minutes < SHORT_LAYOVER_MIN && ' · 时间较紧'}
                      </span>
                    )}
                  </p>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SliceRow;
